import { useEffect, useState } from "react";
import { Menu, X, MessageCircle, ArrowUpRight } from "lucide-react";
import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import { buildWhatsAppUrl } from "@/lib/constants";
import { trackWaClick } from "@/lib/track";
import Wordmark from "./Wordmark";

const navItems = (t) => [
  { href: "#lineas", label: t("nav.lineas") },
  { href: "#diferencial", label: t("nav.diferencial") },
  { href: "#cobertura", label: t("nav.cobertura") },
  { href: "#proceso", label: t("nav.proceso") },
];

export const Navigation = () => {
  const { locale, setLocale } = useSite();
  const { t } = useT(locale);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 24);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = navItems(t);

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
      style={{
        background: scrolled ? "rgba(11, 14, 30, 0.92)" : "transparent",
        borderBottom: scrolled ? "1px solid rgba(251,247,238,0.08)" : "1px solid transparent",
      }}
    >
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14 h-[68px] flex items-center justify-between">
        <a href="#top" data-testid="nav-home-link" aria-label="Incubatour Experiences Lab">
          <Wordmark tone="light" />
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {items.map((it) => (
            <a
              key={it.href}
              href={it.href}
              className="ix-link ix-mono text-[0.7rem] tracking-[0.18em] uppercase text-[var(--ix-cream)]/70 hover:text-[var(--ix-cream)]"
              data-testid={`nav-link-${it.href.replace("#", "")}`}
            >
              {it.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div
            className="flex items-center ix-mono text-[0.66rem] tracking-[0.18em] uppercase"
            data-testid="locale-switch"
          >
            <button
              onClick={() => setLocale("es")}
              className={`px-1.5 py-1 transition-colors ${locale === "es" ? "text-[var(--ix-cream)]" : "text-[var(--ix-cream)]/35 hover:text-[var(--ix-cream)]/70"}`}
              data-testid="locale-es"
              aria-pressed={locale === "es"}
            >
              ES
            </button>
            <span className="text-[var(--ix-cream)]/30">/</span>
            <button
              onClick={() => setLocale("en")}
              className={`px-1.5 py-1 transition-colors ${locale === "en" ? "text-[var(--ix-cream)]" : "text-[var(--ix-cream)]/35 hover:text-[var(--ix-cream)]/70"}`}
              data-testid="locale-en"
              aria-pressed={locale === "en"}
            >
              EN
            </button>
          </div>

          <a
            href={buildWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWaClick("nav_desktop")}
            className="ix-btn ix-btn-grad"
            data-testid="nav-whatsapp-cta"
          >
            <MessageCircle size={14} strokeWidth={1.8} />
            {t("nav.whatsapp")}
            <ArrowUpRight size={13} strokeWidth={1.8} />
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-[var(--ix-cream)]"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${open ? "max-h-[560px] opacity-100" : "max-h-0 opacity-0"}`}
        style={{ background: "rgba(11,14,30,0.97)", borderBottom: "1px solid rgba(251,247,238,0.1)" }}
        data-testid="nav-mobile-panel"
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {items.map((it) => (
            <a
              key={it.href}
              href={it.href}
              onClick={() => setOpen(false)}
              className="py-2.5 ix-display text-2xl text-[var(--ix-cream)]"
              data-testid={`mobile-nav-${it.href.replace("#", "")}`}
            >
              {it.label}
            </a>
          ))}
          <div className="ix-rule-dark my-3 h-px" />
          <div className="flex items-center justify-between">
            <div className="flex items-center ix-mono text-[0.7rem] tracking-[0.18em] uppercase">
              <button
                onClick={() => setLocale("es")}
                className={`px-2 py-1 ${locale === "es" ? "text-[var(--ix-cream)]" : "text-[var(--ix-cream)]/40"}`}
                data-testid="mobile-locale-es"
              >ES</button>
              <span className="text-[var(--ix-cream)]/30">/</span>
              <button
                onClick={() => setLocale("en")}
                className={`px-2 py-1 ${locale === "en" ? "text-[var(--ix-cream)]" : "text-[var(--ix-cream)]/40"}`}
                data-testid="mobile-locale-en"
              >EN</button>
            </div>
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWaClick("nav_mobile")}
              className="ix-btn ix-btn-grad"
              data-testid="mobile-whatsapp-cta"
            >
              <MessageCircle size={14} />
              {t("nav.whatsapp")}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
